import { isComplianceSubdomain } from './getSubdomain';
import { UsCpscComplianceType } from '../types/shipment';
import { isComplianceRequired } from './euCompliance';
import { hasAdminAccess } from '../services/hasAdminAccess';

/**
 * Normalizes a country identifier or name and checks whether it corresponds to the United States.
 * Supports all common aliases: US, USA, United States, United States of America, U.S., U.S.A., America.
 */
export function isUSDestination(countryInput?: any): boolean {
  if (!countryInput) return false;

  let raw = '';
  if (typeof countryInput === 'string') {
    raw = countryInput;
  } else if (typeof countryInput === 'object') {
    raw = countryInput.country?.name || countryInput.name || countryInput.country || '';
  }

  if (typeof raw !== 'string') return false;

  const normalized = raw
    .trim()
    .toLowerCase()
    .replace(/\./g, '') // strip dots: 'u.s.' -> 'us', 'u.s.a.' -> 'usa'
    .replace(/\s+/g, ' '); // collapse spaces

  const US_ALIASES = new Set(['us', 'usa', 'united states', 'united states of america', 'america']);

  return US_ALIASES.has(normalized);
}

export const CPSC_DISCLAIM_CODE_OPTIONS = [
  { display: 'A · Not regulated by CPSC (default)', value: 'Not regulated by CPSC' },
  { display: 'B · CPSC enforcement discretion (rare)', value: 'CPSC enforcement discretion (rare)' },
];

export const CPSC_DEFAULT_DISCLAIM_CODE = 'Not regulated by CPSC';

export const CPSC_SPECIFIC_INTENDED_USE_CODE = '980.000';

export function isAdditionalContextRequiredForCpsc(code?: string | null): boolean {
  return (code || '').trim() === CPSC_SPECIFIC_INTENDED_USE_CODE;
}

/**
 * Normalizes legacy CPSC disclaim code values ('A' / 'B') to the new full display strings.
 */
export function normalizeCpscDisclaimCode(code?: string | null): string {
  if (!code) return '';
  const trimmed = code.trim();
  if (trimmed === 'A' || trimmed === 'Not regulated by CPSC' || trimmed.startsWith('A ·') || trimmed.startsWith('A.')) {
    return 'Not regulated by CPSC';
  }
  if (
    trimmed === 'B' ||
    trimmed === 'CPSC enforcement discretion (rare)' ||
    trimmed.startsWith('B ·') ||
    trimmed.startsWith('B.')
  ) {
    return 'CPSC enforcement discretion (rare)';
  }
  return trimmed;
}

export const CPSC_PRODUCT_ID_TYPE_OPTIONS = [
  { display: 'SKU', value: 'SKU' },
  { display: 'Part number', value: 'Part number' },
  { display: 'Registered number', value: 'Registered number' },
  { display: 'Other', value: 'Other' },
];

export const defaultCpscComplianceState: UsCpscComplianceType = {};

export type CpscFieldKey =
  | 'cpscBeingSold'
  | 'cpscDisclaimCode'
  | 'cpscIntendedUseCode'
  | 'cpscAdditionalContext'
  | 'cpscCertificateFiled'
  | 'cpscProductIdRegistry'
  | 'cpscCertificateVersionId'
  | 'cpscCertifierId'
  | 'cpscProductId'
  | 'cpscProductIdType';

/**
 * Robust boolean parser handling booleans, strings ('true', 'false', 'yes', 'no'), and numbers (1, 0).
 */
export function parseBoolean(val: any): boolean | undefined {
  if (val === true || val === 'true' || val === 'yes' || val === 'Yes' || val === 1 || val === '1') return true;
  if (val === false || val === 'false' || val === 'no' || val === 'No' || val === 0 || val === '0') return false;
  return undefined;
}

/**
 * Returns the exact set of required US CPSC field keys for the active branch.
 */
export function getActiveUsCpscRequiredFields(
  cpsc?: UsCpscComplianceType | null,
  isUS = true,
  isRequired?: boolean,
  isAdmin?: boolean,
): CpscFieldKey[] {
  if (!isUS) return [];
  const complianceRequired = typeof isRequired === 'boolean' ? isRequired : isComplianceRequired();
  if (!complianceRequired) return [];

  const effectiveCpsc = cpsc || defaultCpscComplianceState;
  const beingSold = parseBoolean(effectiveCpsc.beingSold);

  if (beingSold === undefined) {
    return ['cpscBeingSold'];
  }

  if (beingSold === false) {
    const fields: CpscFieldKey[] = ['cpscDisclaimCode'];
    const adminMode = typeof isAdmin === 'boolean' ? isAdmin : hasAdminAccess();
    if (adminMode) {
      fields.push('cpscIntendedUseCode');
    }
    if (isAdditionalContextRequiredForCpsc(effectiveCpsc.intendedUseCode)) {
      fields.push('cpscAdditionalContext');
    }
    return fields;
  }

  const certificateFiled = parseBoolean(effectiveCpsc.certificateFiled);
  if (certificateFiled === undefined) {
    return ['cpscCertificateFiled'];
  }

  if (certificateFiled === true) {
    return ['cpscCertificateFiled', 'cpscProductIdRegistry', 'cpscCertificateVersionId', 'cpscCertifierId'];
  }

  return ['cpscCertificateFiled'];
}

export interface CpscValidationResult {
  isValid: boolean;
  errors: { [key: string]: string };
}

/**
 * Validates US CPSC compliance fields according to the active conditional branch.
 */
export function validateCpscCompliance(
  cpsc?: UsCpscComplianceType | null,
  isUS = true,
  isRequired?: boolean,
  isAdmin?: boolean,
): CpscValidationResult {
  const errors: { [key: string]: string } = {};
  if (!isUS) {
    return { isValid: true, errors: {} };
  }

  const complianceRequired = typeof isRequired === 'boolean' ? isRequired : isComplianceRequired();
  if (!complianceRequired) {
    return { isValid: true, errors: {} };
  }

  const effectiveCpsc = cpsc || defaultCpscComplianceState;
  const adminMode = typeof isAdmin === 'boolean' ? isAdmin : hasAdminAccess();
  const activeRequiredFields = getActiveUsCpscRequiredFields(effectiveCpsc, isUS, isRequired, adminMode);

  for (const field of activeRequiredFields) {
    switch (field) {
      case 'cpscBeingSold': {
        const beingSold = parseBoolean(effectiveCpsc.beingSold);
        if (beingSold === undefined) {
          errors.cpscBeingSold = 'Please specify if this item needs a US safety certificate (GCC or CPC)';
        }
        break;
      }
      case 'cpscDisclaimCode': {
        const disclaim = effectiveCpsc.disclaimCode?.trim();
        if (!disclaim) {
          errors.cpscDisclaimCode = 'Please select a Disclaim code';
        }
        break;
      }
      case 'cpscIntendedUseCode': {
        const code = effectiveCpsc.intendedUseCode?.trim() || '';
        if (!code) {
          errors.cpscIntendedUseCode = 'Intended Use Code is required';
        }
        break;
      }
      case 'cpscAdditionalContext': {
        const context = effectiveCpsc.additionalContext?.trim() || '';
        if (!context) {
          errors.cpscAdditionalContext = 'Additional Context is required';
        }
        break;
      }
      case 'cpscCertificateFiled': {
        const certFiled = parseBoolean(effectiveCpsc.certificateFiled);
        if (certFiled === undefined) {
          errors.cpscCertificateFiled = 'Please specify if the certificate is filed in the CPSC Product Registry';
        }
        break;
      }
      case 'cpscProductIdRegistry': {
        if (!effectiveCpsc.productIdRegistry?.trim()) {
          errors.cpscProductIdRegistry = 'Product ID (registry) is required';
        }
        break;
      }
      case 'cpscCertificateVersionId': {
        if (!effectiveCpsc.certificateVersionId?.trim()) {
          errors.cpscCertificateVersionId = 'Certificate Version ID is required';
        }
        break;
      }
      case 'cpscCertifierId': {
        if (!effectiveCpsc.certifierId?.trim()) {
          errors.cpscCertifierId = 'Certifier ID is required';
        }
        break;
      }
      case 'cpscProductId': {
        if (!effectiveCpsc.productId?.trim()) {
          errors.cpscProductId = 'Product ID is required';
        }
        break;
      }
      case 'cpscProductIdType': {
        if (!effectiveCpsc.productIdType?.trim()) {
          errors.cpscProductIdType = 'Product ID type is required';
        }
        break;
      }
    }
  }

  // Length constraints for No-branch fields (applied whenever populated)
  const beingSold = parseBoolean(effectiveCpsc.beingSold) ?? true;
  if (beingSold === false) {
    const code = effectiveCpsc.intendedUseCode?.trim() || '';
    if (code.length > 16) {
      errors.cpscIntendedUseCode = 'Intended Use Code must be 16 characters or fewer';
    }
    const context = effectiveCpsc.additionalContext?.trim() || '';
    if (context.length > 22) {
      errors.cpscAdditionalContext = 'Additional Context must be 22 characters or fewer';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Builds the sanitized US CPSC payload, omitting unselected branch keys.
 */
export function buildCpscPayload(
  cpsc?: UsCpscComplianceType | null,
  isUS = true,
  fallbackProduct?: { productId?: string; productIdType?: string },
): UsCpscComplianceType | undefined {
  if (!isUS || !cpsc) return undefined;

  const beingSold = parseBoolean(cpsc.beingSold);
  if (beingSold === undefined) {
    return undefined;
  }

  if (!beingSold) {
    const normalizedDisclaim = normalizeCpscDisclaimCode(cpsc.disclaimCode);
    const res: UsCpscComplianceType = {
      beingSold: false,
      ...(normalizedDisclaim ? { disclaimCode: normalizedDisclaim } : {}),
    };
    if (cpsc.intendedUseCode?.trim()) {
      res.intendedUseCode = cpsc.intendedUseCode.trim();
    }
    if (cpsc.additionalContext?.trim()) {
      res.additionalContext = cpsc.additionalContext.trim();
    }
    return res;
  }

  const certificateFiled = parseBoolean(cpsc.certificateFiled);
  if (certificateFiled === true) {
    return {
      beingSold: true,
      certificateFiled: true,
      productIdRegistry: cpsc.productIdRegistry?.trim() || '',
      certificateVersionId: cpsc.certificateVersionId?.trim() || '',
      certifierId: cpsc.certifierId?.trim() || '',
    };
  }

  return {
    beingSold: true,
    ...(certificateFiled !== undefined ? { certificateFiled } : {}),
    productId: (cpsc.productId || fallbackProduct?.productId || '').trim(),
    productIdType: (cpsc.productIdType || fallbackProduct?.productIdType || 'SKU').trim(),
  };
}

/**
 * Centrally resets US CPSC fields when visibility/branch triggers change.
 */
export function resetCpscConditionalFields(
  cpsc: UsCpscComplianceType = defaultCpscComplianceState,
  trigger:
    | 'beingSold_to_false'
    | 'beingSold_to_true'
    | 'certificateFiled_to_false'
    | 'country_to_non_us'
    | 'mode_away_from_c2c'
    | 'shipment_type_document',
): UsCpscComplianceType {
  const current = { ...cpsc };
  switch (trigger) {
    case 'beingSold_to_false':
      return {
        beingSold: false,
        disclaimCode: normalizeCpscDisclaimCode(current.disclaimCode) || undefined,
        intendedUseCode: '',
        additionalContext: '',
        certificateFiled: undefined,
        productIdRegistry: '',
        certificateVersionId: '',
        certifierId: '',
      };
    case 'beingSold_to_true':
      return {
        beingSold: true,
        certificateFiled: undefined,
        productIdRegistry: '',
        certificateVersionId: '',
        certifierId: '',
        disclaimCode: undefined,
        intendedUseCode: '',
        additionalContext: '',
        productId: current.productId || '',
        productIdType: current.productIdType || 'SKU',
      };
    case 'certificateFiled_to_false':
      return {
        ...current,
        beingSold: true,
        certificateFiled: false,
        productIdRegistry: '',
        certificateVersionId: '',
        certifierId: '',
      };
    case 'country_to_non_us':
    case 'mode_away_from_c2c':
    case 'shipment_type_document':
      return {
        beingSold: undefined,
        certificateFiled: undefined,
        productIdRegistry: '',
        certificateVersionId: '',
        certifierId: '',
        disclaimCode: undefined,
        intendedUseCode: '',
        additionalContext: '',
        productId: '',
        productIdType: 'SKU',
      };
  }
}

export interface BackendCpscPayload {
  isRegulated?: boolean;
  isRegistered?: boolean;
  disclaimMessageSet?: {
    disclaimCode?: 'A' | 'B';
    intendedUseCode: string;
    intendedUseDescription: string;
  };
  referenceMessageSet?: {
    productVersion: string;
    certifierId: string;
    registryProductId: string;
  };
}

export function toBackendDisclaimCode(code?: string | null): 'A' | 'B' | undefined {
  if (!code) return undefined;
  const trimmed = code.trim();
  if (trimmed === 'A' || trimmed === 'Not regulated by CPSC' || trimmed.startsWith('A ·') || trimmed.startsWith('A.')) {
    return 'A';
  }
  if (
    trimmed === 'B' ||
    trimmed === 'CPSC enforcement discretion (rare)' ||
    trimmed.startsWith('B ·') ||
    trimmed.startsWith('B.')
  ) {
    return 'B';
  }
  return undefined;
}

/**
 * Builds product-level US CPSC fields matching the backend schema:
 * isRegulated, isRegistered, disclaimMessageSet, referenceMessageSet
 */
export function buildBackendCpscProductPayload(
  cpsc?: UsCpscComplianceType | null,
  isUS = true,
  customHostname?: string,
): BackendCpscPayload | undefined {
  if (!isComplianceSubdomain(customHostname) || !isUS || !cpsc) return undefined;
  const beingSold = parseBoolean(cpsc.beingSold);

  if (beingSold === undefined) return undefined;

  const certificateFiled = parseBoolean(cpsc.certificateFiled);

  if (!beingSold) {
    const rawDisclaim = cpsc.disclaimCode?.trim() || '';
    const disclaimCode = toBackendDisclaimCode(rawDisclaim);
    const intendedUseCode = cpsc.intendedUseCode?.trim() || '';
    const intendedUseDescription = cpsc.additionalContext?.trim() || '';
    const hasDisclaimContent = Boolean(disclaimCode || intendedUseCode || intendedUseDescription);

    return {
      isRegulated: false,
      ...(certificateFiled !== undefined ? { isRegistered: certificateFiled } : {}),
      ...(hasDisclaimContent
        ? {
            disclaimMessageSet: {
              ...(disclaimCode ? { disclaimCode } : {}),
              intendedUseCode,
              intendedUseDescription,
            },
          }
        : {}),
    };
  }

  const registryProductId = cpsc.productIdRegistry?.trim() || '';
  const productVersion = cpsc.certificateVersionId?.trim() || '';
  const certifierId = cpsc.certifierId?.trim() || '';
  const hasReferenceContent = Boolean(registryProductId || productVersion || certifierId);

  return {
    isRegulated: true,
    ...(certificateFiled !== undefined ? { isRegistered: certificateFiled } : {}),
    ...(certificateFiled === true && hasReferenceContent
      ? {
          referenceMessageSet: {
            registryProductId,
            productVersion,
            certifierId,
          },
        }
      : {}),
  };
}

/**
 * Applies reactive auto-set for US CPSC beingSold field:
 * When destination is US, Shipment Mode is C2C, and Purpose Of Shipment is Sample or Gift,
 * all product rows in boxes are updated to beingSold = false, with Yes-branch fields cleared.
 */
/**
 * Evaluates whether the US CPSC auto-set condition (C2C + Sample/Gift + US destination) is met.
 */
export function isCpscAutoSetConditionMet(
  destCountry?: string | null,
  shipmentMode?: string | null,
  purposeOfShipment?: string | null,
  customHostname?: string,
): boolean {
  if (!isComplianceSubdomain(customHostname)) return false;
  const isUS = isUSDestination(destCountry);
  const isC2C = (shipmentMode || '').trim().toLowerCase() === 'c2c';
  const cleanPurpose = (purposeOfShipment || '').trim().toLowerCase();
  const isSampleOrGift = cleanPurpose === 'sample' || cleanPurpose === 'gift';
  return Boolean(isUS && isC2C && isSampleOrGift);
}

/**
 * Determines if the auto-set should trigger based on state transition.
 * Only triggers when transitioning from false -> true, preventing override of manual user changes.
 */
export function shouldTriggerCpscAutoSet(prevConditionMet: boolean, currentConditionMet: boolean): boolean {
  return currentConditionMet && !prevConditionMet;
}

export function applyCpscAutoSetForC2CSampleGift(
  boxes: any[] = [],
  shipmentMode?: string | null,
  purposeOfShipment?: string | null,
  destCountry?: string | null,
): { updatedBoxes: any[]; didUpdate: boolean } {
  if (
    !isCpscAutoSetConditionMet(destCountry, shipmentMode, purposeOfShipment) ||
    !Array.isArray(boxes) ||
    boxes.length === 0
  ) {
    return { updatedBoxes: boxes, didUpdate: false };
  }

  const updatedBoxes = boxes.map((box: any) => ({
    ...box,
    products: (box.products || []).map((p: any) => {
      const cpsc = p.cpscCompliance || {};
      const reset = resetCpscConditionalFields(cpsc, 'beingSold_to_false');
      return {
        ...p,
        cpscCompliance: reset,
      };
    }),
  }));

  return { updatedBoxes, didUpdate: true };
}

/**
 * Clears US CPSC beingSold and sub-branch fields to unselected when switching away from C2C.
 */
export function applyCpscClearWhenLeavingC2C(boxes: any[] = []): { updatedBoxes: any[]; didUpdate: boolean } {
  if (!Array.isArray(boxes) || boxes.length === 0) {
    return { updatedBoxes: boxes, didUpdate: false };
  }
  let didUpdate = false;
  const updatedBoxes = boxes.map((box: any) => ({
    ...box,
    products: (box.products || []).map((p: any) => {
      if (p.cpscCompliance && p.cpscCompliance.beingSold !== undefined) {
        didUpdate = true;
        return {
          ...p,
          cpscCompliance: resetCpscConditionalFields(p.cpscCompliance, 'mode_away_from_c2c'),
        };
      }
      return p;
    }),
  }));
  return { updatedBoxes, didUpdate };
}
